<script lang="ts" setup>
import { AssignmentsService } from "classes/models/assignments/assignments.service";
import { Subscription } from "rxjs";
import { Checklists } from "types/Checklist";
import { Assignment } from "~/classes/models/assignments/assignment.model";

const emit = defineEmits(["close"]);

const userChecklistStore = useUserChecklistsStore();
const { checklists } = storeToRefs(userChecklistStore);

const subscription = ref<Subscription | undefined>();

const userClassroomsData = useUserClassroomsDataStore();
const { assignmentsWithoutChecklistGroupedByClassroom } =
  storeToRefs(userClassroomsData);

const isSaving = ref(false);
const showErrors = ref(false);

const saveAssignments = async () => {
  showErrors.value = false;

  const originalAssignmentsMissingChecklists = Object.values(
    assignmentsWithoutChecklistGroupedByClassroom.value
  ).flat();

  /// first hceck if any assignments are missing a checklist
  const assignmentsWithChecklists = updatedAssignments.value.filter(
    (assignment) => assignment.checklistId != undefined
  );

  if (
    assignmentsWithChecklists.length <
    originalAssignmentsMissingChecklists.length
  ) {
    useBaseToast("Please select a checklist for each assignment.", "error");
    showErrors.value = true;
    return;
  }

  isSaving.value = true;

  AssignmentsService.saveAssignments(updatedAssignments.value);

  isSaving.value = false;

  useBaseToast("Assignments saved!", "success");
  emit("close");
};

const checklistPath = computed(() => {
  return (checklistId: string) => {
    return (
      checklists.value.find((c: Checklist) => c.id === checklistId)?.path ??
      "/checklists"
    );
  };
});

const sortedChecklists = computed(() => {
  return useSortChecklists(checklists.value);
});

const updatedAssignments = ref<Assignment[]>([]);
const updateAssigmentChecklist = (
  checklistId: string,
  assignment: Assignment
) => {
  const assignmentCopy = Assignment.fromMap(assignment.toMap());
  assignmentCopy.checklistId = checklistId;
  assignmentCopy.checklistPath = checklistPath.value(checklistId);
  updatedAssignments.value.push(assignmentCopy);
};

const assignmentChecklist = computed(() => {
  return (assignmentId: string) => {
    return updatedAssignments.value.find((a) => a.id === assignmentId)
      ?.checklistId;
  };
});
</script>

<template>
  <div class="bg-surface rounded flex flex-col h-full p-4">
    <BaseLabelBadge color="danger">
      <template #icon>
        <BaseIconInfo />
      </template>
      Some of your assignments are missing checklists. Assign a checklist to
      ensure student documents are imported correctly.
    </BaseLabelBadge>
    <div class="grow min-h-0 overflow-auto flex flex-col">
      <div
        v-for="(
          assignments, classroomName
        ) in assignmentsWithoutChecklistGroupedByClassroom"
        :key="classroomName"
        class="flex flex-col my-4"
      >
        <h2 class="font-bold text-h6 border-b py-2 mb-2">
          {{ classroomName }}
        </h2>
        <div
          v-for="assignment in useSortAssignments(assignments)"
          :key="assignment.id"
          class="flex flex-col p-4 mb-2"
          :class="{
            ' bg-danger-default bg-opacity-10 rounded':
              showErrors &&
              updatedAssignments.find((a) => a.id === assignment.id)
                ?.checklistId == undefined,
          }"
        >
          <label class="text-body-1 font-bold mb-2">
            Assignment: {{ assignment.name }}
          </label>
          <ChecklistsSelect
            :model-value="assignmentChecklist(assignment.id!)"
            :options="sortedChecklists"
            placeholder="Choose a checklist..."
            value-prop="id"
            label="title"
            :searchable="true"
            @update:model-value="updateAssigmentChecklist($event, assignment)"
          />
        </div>
      </div>
      <div class="flex flex-row justify-start py-2 mt-2 border-t">
        <BaseButton
          class="w-[200px]"
          :show-spinner="isSaving"
          @click="saveAssignments"
        >
          Save
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style>
.assignment-checklists-modal {
  z-index: 9999 !important;
}
.assignment-checklists-modal-content {
  display: flex;
  flex-direction: column;

  width: 600px;
  height: 90%;
  margin: auto;
  @apply rounded;
  margin-top: 64px;
}
</style>
